import axios from "axios";
import {
  CardStatusResponse,
  SumSubTokenResponse,
  CreditDelegationRequest,
  CreditDelegationResponse,
  LinkCardRequest,
  UserCardsResponse,
  CreditInformationResponse,
  CreateCardResponse,
  CardSecretResponse,
  TransactionsResponse,
  OnchainTransactionDetailsResponse,
  CardActionType,
  UpdateCardRequest,
  CreditType,
  CardLimitFrequency,
  EulerTotalAssetResponse,
} from "./types";
import { getApiClient } from "./httpClient";
import {
  formatAvailableLimit,
  generateUUID,
  getCardSolverConfig,
  mapEulerMetadataToSuppliedAssets,
  waitFor,
} from "./utils";
import {
  getMockCardStatus,
  getMockUserCards,
  getMockAaveCreditInformation,
  getMockEulerCreditInformation,
  getMockSumSubToken,
  getMockCreditDelegationTxData,
  getMockTransactions,
  transactionsMockData,
} from "./mockData";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { getAuthState } from "./auth";

export const getCardStatus = async (
  eoaAddress: string
): Promise<CardStatusResponse> => {
  // Return mock data
  // return getMockCardStatus();

  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();
    const response = await client.get<CardStatusResponse>(`/cards/user/status`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return { data: "userNotExisting" };
    }
    throw error;
  }
};

export const getUserCards = async (
  eoaAddress: string
): Promise<UserCardsResponse> => {
  // Return mock data
  // return getMockUserCards();

  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();
    const response = await client.get<UserCardsResponse>(`/cards/user`);
    return response.data;
  } catch (err) {
    console.error("[getUserCards]", err);
    return { data: null };
  }
};

export const getCreditInformation = async (
  creditAddress: string,
  solverType: "aave-v3" | "euler" | "hypurrFi" | string
): Promise<CreditInformationResponse> => {
  // Return mock data based on credit type
  // if (creditType === "aave-v3") {
  //   return getMockAaveCreditInformation();
  // } else {
  //   return getMockEulerCreditInformation();
  // }

  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();
    const response = await client.post<CreditInformationResponse>(
      `/cards/credit`,
      {
        solverType,
        config:
          solverType === "aave-v3"
            ? {
                borrowTokenAddress:
                  "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913",
              }
            : solverType === "hypurrFi"
            ? {
                borrowTokenAddress:
                  "0xb8ce59fc3717ada4c02eadf9682a9e934f625ebb",
              }
            : {
                borrowVaultAddress:
                  "0x0A1a3b5f2041F33522C4efc754a7D096f880eE16",
              },
      }
    );

    // Transform availableLimit if it exists
    if (
      response.data?.data &&
      typeof response.data.data.availableLimit === "number"
    ) {
      response.data.data.availableLimit = formatAvailableLimit(
        response.data.data.availableLimit,
        solverType
      );
      response.data.data.amountDelegated = formatAvailableLimit(
        response.data.data.amountDelegated,
        solverType
      );
    }

    // If Euler, map vaults to suppliedAssets for compatibility (without mutating original)
    if (solverType === "euler" && response.data?.data?.metadata) {
      response.data.data.metadata = mapEulerMetadataToSuppliedAssets(
        response.data.data.metadata
      );
    }

    return response.data;
  } catch (error) {
    console.error("[getCreditInformation]", error);
    return { data: null };
  }
};

export const getSumSubAccessToken = async (
  userId: string,
  ipAddress?: string
): Promise<SumSubTokenResponse["data"] | undefined> => {
  // Return mock data
  // return getMockSumSubToken();

  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();
    const response = await client.post<SumSubTokenResponse>(
      "/cards/sumsub/token",
      { userId, ipAddress }
    );
    return response.data.data;
  } catch (error) {
    console.error("[error at getSumSubAccessToken]", error);
    return undefined;
  }
};

export const getCreditDelegationTxData = async (
  requestData: CreditDelegationRequest
): Promise<{ data: CreditDelegationResponse }> => {
  // Return mock data
  // return getMockCreditDelegationTxData();

  // Real API call (commented out - uncomment to use real API
  const eoa = requestData.from;
  const payload = {
    creditType: requestData.creditType,
    creditLimit: requestData.creditLimit,
    userParams: requestData?.userParams,
  };
  const client = getApiClient();
  const response = await client.post<{ data: CreditDelegationResponse }>(
    `/cards/credit/delegate`,
    payload
  );
  return response.data;
};

export const linkCardToCreditAccount = async (
  requestData: LinkCardRequest
): Promise<any> => {
  const client = getApiClient();
  const response = await client.patch("/cards/", requestData);
  return response.data;
};

/**
 * Updates a user's card by linking it to a credit provider using signed authorization.
 *
 * @param requestData - Request payload including credit provider details and EIP-712 auth data.
 * @returns An object indicating success or failure, with an optional message.
 */
export const updateCard = async (
  requestData: UpdateCardRequest
): Promise<{ success: boolean; message?: string }> => {
  const { apiKey, baseUrl, apiPath } = useSDKConfigStore.getState();

  try {
    const client = axios.create({
      baseURL: `${baseUrl}${apiPath}`,
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
        Authorization: `Bearer ${requestData.auth}`,
        sessionID: generateUUID(),
      },
    });

    const response = await client.patch(`/cards/creditprovider`, {
      creditProviderAddress: requestData.creditProviderAddress,
      chainId: requestData.chainId,
      creditType: requestData.creditType,
      cardSolverConfig: getCardSolverConfig(requestData.creditType),
    });

    if (response.status === 200) {
      return { success: true };
    }

    return {
      success: false,
      message: response.data?.message || "Failed to update card",
    };
  } catch (error: any) {
    console.error("[updateCard]", error);
    return {
      success: false,
      message:
        error?.response?.data?.data?.error ||
        error?.message ||
        "Failed to update card",
    };
  }
};

export const createCard = async (
  userEoa: string,
  chainID: number,
  creditType: CreditType,
  auth: string
): Promise<CreateCardResponse> => {
  const { apiKey, baseUrl, apiPath } = useSDKConfigStore.getState();

  const client = axios.create({
    baseURL: `${baseUrl}${apiPath}`,
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      Authorization: `Bearer ${auth}`,
      sessionID: generateUUID(),
    },
  });

  const response = await client.post<CreateCardResponse>(`/cards/create`, {
    creditProviderAddress: userEoa,
    chainID: chainID,
    creditType: creditType,
    cardSolverConfig: getCardSolverConfig(creditType),
  });

  return response.data;
};

/**
 * Freezes or unfreezes a user's card using a signed request.
 *
 * @param eoa - The Ethereum address of the user.
 * @param cardID - The UUID of the card to be frozen/unfrozen.
 * @param chainId - The blockchain network's chain ID.
 * @param signature - EIP-712 signature for the freeze/unfreeze action.
 * @param timestamp - UNIX timestamp (in seconds) of the request.
 * @param freeze - Boolean flag indicating whether to freeze (true) or unfreeze (false) the card.
 * @param action - Action type string, e.g., "freeze" or "unfreeze", used in request headers.
 * @returns An object indicating success or failure with an optional error message.
 */
export const freezeCard = async (
  eoa: string,
  cardID: string,
  chainID: number,
  signature: string,
  timestamp: number,
  freeze: boolean,
  action: CardActionType
): Promise<{ success: boolean; message?: string }> => {
  const { apiKey, baseUrl, apiPath } = useSDKConfigStore.getState();

  const authJson = {
    cardID,
    action,
    version: "1",
    timestamp,
    chainID,
    sig: signature,
    userAddr: eoa,
  };

  const encodedAuth = btoa(JSON.stringify(authJson));

  const client = axios.create({
    baseURL: `${baseUrl}${apiPath}`,
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      Authorization: `Bearer ${encodedAuth}`,
      sessionID: generateUUID(),
    },
  });

  try {
    const response = await client.patch(`/cards/status`, {
      cardID,
      freeze,
    });
    // if 200 then return success
    if (response.status === 200) {
      return { success: true };
    }
    // if not 200, return failure with message
    return {
      success: false,
      message: response.data?.message || "Failed to freeze card",
    };
  } catch (error: any) {
    console.error("[freezeCard]", error);
    return {
      success: false,
      message:
        error?.response?.data?.data?.error ||
        error?.message ||
        "Failed to freeze card",
    };
  }
};

/**
 * Updates the PIN of a card by sending encrypted PIN data using a signed request.
 *
 * @param eoa - The Ethereum address of the user.
 * @param cardID - The UUID of the card whose PIN is to be updated.
 * @param chainId - The blockchain network's chain ID.
 * @param sessionID - The session ID generated by the client (e.g., via Rain).
 * @param signature - EIP-712 signature for the PIN update action.
 * @param timestamp - UNIX timestamp (in seconds) of the request.
 * @param iv - Base64-encoded Initialization Vector (IV) used to encrypt the PIN.
 * @param data - Base64-encoded encrypted PIN data.
 * @param action - Action type string used in request headers.
 * @returns An object indicating success or failure with an optional error message.
 */
export const updatePin = async (
  eoa: string,
  cardID: string,
  chainID: number,
  sessionID: string,
  signature: string,
  timestamp: number,
  iv: string,
  data: string,
  action: CardActionType
): Promise<{ success: boolean; message?: string }> => {
  const { apiKey, baseUrl, apiPath } = useSDKConfigStore.getState();

  const authJson = {
    cardID,
    action,
    version: "1",
    timestamp,
    chainID,
    sig: signature,
    userAddr: eoa,
  };

  const encodedAuth = btoa(JSON.stringify(authJson));

  const client = axios.create({
    baseURL: `${baseUrl}${apiPath}`,
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      Authorization: `Bearer ${encodedAuth}`,
      sessionID,
    },
  });

  try {
    const response = await client.patch<{ success: boolean; message?: string }>(
      `/cards/pin`,
      { pin: { encryptedPin: { data, iv } } }
    );

    if (response.status === 200) {
      return { success: true };
    }

    return {
      success: false,
      message: response.data?.message || "Failed to update PIN",
    };
  } catch (error: any) {
    console.error("[updatePin]", error);
    return {
      success: false,
      message:
        error?.response?.data?.data?.error ||
        error?.message ||
        "Failed to update PIN",
    };
  }
};

/**
 * Get encrypted card details for a user
 * @param eoa Ethereum address of the user
 * @param cardID UUID of the card
 * @param chainId Blockchain chain ID
 * @param sessionID Session identifier
 * @param signature EIP-712 signature
 * @param timestamp UNIX timestamp (in seconds)
 * @returns Encrypted PAN and CVC data
 */
export const getCardSecrets = async (
  eoa: string,
  cardID: string,
  chainID: number,
  sessionID: string,
  signature: string,
  timestamp: number,
  action: CardActionType
): Promise<{
  encryptedPan: { iv: string; data: string };
  encryptedCvc: { iv: string; data: string };
  ExpirationMonth: string;
  ExpirationYear: string;
}> => {
  const { apiKey, baseUrl, apiPath } = useSDKConfigStore.getState();

  const authJson = {
    cardID,
    action,
    version: "1",
    timestamp,
    chainID,
    sig: signature,
    userAddr: eoa,
  };

  const encodedAuth = btoa(JSON.stringify(authJson));

  const client = axios.create({
    baseURL: `${baseUrl}${apiPath}`,
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      Authorization: `Bearer ${encodedAuth}`,
      sessionID,
    },
  });

  try {
    const response = await client.get(`/cards/secrets`);

    // If successful
    if (response.status === 200 && response.data?.data) {
      return response.data.data;
    }

    // Fallback if 200 but no data
    throw new Error("Unexpected response format while fetching card secrets.");
  } catch (error: any) {
    console.error("[getCardSecrets] Error fetching card secrets:", error);

    const errorMessage =
      error?.response?.data?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch card secrets";

    throw new Error(errorMessage);
  }
};

export const getCardLimit = async (
  cardID: string
): Promise<{
  amount: number; // in cents
  frequency: CardLimitFrequency;
}> => {
  const client = getApiClient();

  try {
    const response = await client.get(`/cards/limit/${cardID}`);

    // If successful
    if (response.status === 200 && response.data?.data) {
      return response.data.data;
    }

    // Fallback if 200 but no data
    throw new Error("Unexpected response format while fetching card limits.");
  } catch (error: any) {
    console.error("[getCardLimit] Error fetching card limits:", error);

    const errorMessage =
      error?.response?.data?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch card limits";

    throw new Error(errorMessage);
  }
};

export const updateLimit = async (
  eoa: string,
  cardID: string,
  chainID: number,
  signature: string,
  timestamp: number,
  limit: number,
  action: CardActionType
): Promise<{ success: boolean; message?: string }> => {
  const { apiKey, baseUrl, apiPath } = useSDKConfigStore.getState();

  const authJson = {
    cardID,
    action,
    version: "1",
    timestamp,
    chainID,
    sig: signature,
    userAddr: eoa,
  };

  const encodedAuth = btoa(JSON.stringify(authJson));

  const client = axios.create({
    baseURL: `${baseUrl}${apiPath}`,
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      Authorization: `Bearer ${encodedAuth}`,
    },
  });

  try {
    const response = await client.patch<{ success: boolean; message?: string }>(
      `/cards/limits`,
      { limit, frequency: "per24HourPeriod" }
    );

    if (response.status === 200) {
      return { success: true };
    }

    return {
      success: false,
      message: response.data?.message || "Failed to update limit",
    };
  } catch (error: any) {
    console.error("[updateLimit]", error);
    return {
      success: false,
      message:
        error?.response?.data?.data?.error ||
        error?.message ||
        "Failed to update limit",
    };
  }
};

/**
 * Get the public key for card encryption
 * @returns Public key in PEM format
 */
export const getCardPublicKey = async (): Promise<{ data: string }> => {
  const client = getApiClient();
  const response = await client.get<{ data: string }>("/cards/pubkey");
  return response.data;
};

export const getTransactions = async (
  userAddress: string,
  cardID: string,
  limit?: number,
  cursor?: string
): Promise<TransactionsResponse> => {
  // Return mock data

  // await waitFor(2000);
  // return transactionsMockData;
  // Real API call
  try {
    const client = getApiClient();
    const response = await client.get<import("./types").TransactionsResponse>(
      `cards/transactions/${cardID}`,
      {
        params: {
          limit,
          cursor,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("[getTransactions]", error);
    // Return a default structure on error to prevent crashes
    return {
      data: {
        transactions: [],
        pagination: {
          limit: limit || 50,
          has_next: false,
        },
      },
    };
  }
};

export const getOnchainTransactionDetails = async (
  transactionID: string
): Promise<OnchainTransactionDetailsResponse> => {
  // Return mock data
  // return getMockOnchainTransactionDetails();

  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();
    const response = await client.get<OnchainTransactionDetailsResponse>(
      `cards/transactions/${transactionID}/onchain`
    );
    return response.data;
  } catch (error) {
    console.error("[getOnchainTransactionDetails]", error);
    return { data: [] };
  }
};

export const getUserIsUs = async (): Promise<{ data: boolean }> => {
  try {
    const client = getApiClient();
    const response = await client.get<{ data: boolean }>(`cards/user/is_us`);
    return response.data;
  } catch (error) {
    console.error("[getUserIsUs]", error);
    return { data: false };
  }
};

/**
 * Fetches a preview of the Euler credit limits based on the provided vaults.
 * @param vaults Array of vault addresses to be used for the preview.
 * @returns An object containing credit and collateral amounts as strings.
 */
export const getEulerCardPreview = async (
  vaults: string[],
  creditType: CreditType = "euler"
): Promise<{ data: { credit: string; collateral: string } }> => {
  try {
    const client = getApiClient();
    const response = await client.post<{
      data: { credit: string; collateral: string };
    }>(`/cards/credit/${creditType}/preview`, {
      vaults,
      borrowVaultAddress: "0x0A1a3b5f2041F33522C4efc754a7D096f880eE16",
    });
    return response.data;
  } catch (error) {
    console.error("[getEulerCardPreview]", error);
    return { data: { credit: "0", collateral: "0" } };
  }
};

/**
 * Fetches the Euler health factor for the user.
 * @returns An object containing the health factor as a string.
 */
export const getEulerHealthFactor = async (): Promise<{
  data: { healthFactor: string };
}> => {
  try {
    const client = getApiClient();
    const response = await client.get<{
      data: { healthFactor: string };
    }>(
      `/cards/euler/hf?borrowVaultAddress=0x0A1a3b5f2041F33522C4efc754a7D096f880eE16`
    );
    return response.data;
  } catch (error) {
    console.error("[getEulerHealthFactor]", error);
    return { data: { healthFactor: "0" } };
  }
};

/**
 * Fetches the pending transaction balances for a specific card.
 * @param cardID The UUID of the card for which to fetch pending transaction balances.
 * @returns An object containing the status and balances of pending transactions.
 */
export const getPendingTransactionBalance = async (
  cardID: string
): Promise<{
  data: {
    status: string;
    balances: { usd: number };
  };
}> => {
  try {
    const client = getApiClient();
    const response = await client.get<{
      data: {
        status: string;
        balances: { usd: number };
      };
    }>(`/cards/transactions/balances/pending/${cardID}`);
    return response.data;
  } catch (error) {
    console.error("[getPendingTransactionBalances]", error);
    return { data: { status: "error", balances: { usd: 0 } } };
  }
};

/**
 * Get Euler total assets for a specific chain ID
 * @param chainID - The chain ID to get assets for
 * @returns Promise with total assets in USD
 */
export const getEulerTotalAsset = async (
  chainID: number
): Promise<EulerTotalAssetResponse> => {
  try {
    const client = getApiClient();
    const response = await client.get<EulerTotalAssetResponse>(
      `/cards/euler/assets/${chainID}`
    );
    return response.data;
  } catch (error) {
    console.error("[getEulerTotalAsset]", error);
    return { data: { totalAssetsUSD: 0 } };
  }
};
